import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Sparkles, MessageSquare, BookOpen, Target, ArrowRight, Clock, Zap, Library, Lightbulb, RotateCcw, BadgeEuro } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

export default function Landing() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [pricing, setPricing] = useState({ amount: 29, currency: 'EUR' }); // Fallback

  useEffect(() => {
    // Fetch current pricing from Stripe
    const fetchPricing = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-pricing');
        
        if (error) {
          console.error('Error fetching pricing:', error);
          return;
        }
        
        if (data?.amount) {
          setPricing({
            amount: data.amount,
            currency: data.currency || 'EUR'
          });
        }
      } catch (err) {
        console.error('Failed to fetch pricing:', err);
      }
    };

    fetchPricing();
  }, []);

  const features = [
    {
      icon: MessageSquare,
      title: t('landing.features.available.title'),
      description: t('landing.features.available.description')
    },
    {
      icon: BookOpen,
      title: t('landing.features.knowledge.title'),
      description: t('landing.features.knowledge.description')
    },
    {
      icon: Target,
      title: t('landing.features.practical.title'),
      description: t('landing.features.practical.description')
    }
  ];

  const steps = [
    {
      number: "01",
      title: t('landing.process.step1.title'),
      description: t('landing.process.step1.description')
    },
    {
      number: "02",
      title: t('landing.process.step2.title'),
      description: t('landing.process.step2.description')
    },
    {
      number: "03",
      title: t('landing.process.step3.title'),
      description: t('landing.process.step3.description')
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Language Switcher */}
      <div className="fixed top-6 right-6 z-50">
        <LanguageSwitcher />
      </div>

      {/* Hero Section with Strong Headlines */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary via-primary/95 to-accent">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.1),transparent_50%)]" />
        
        <div className="container relative mx-auto px-4 pt-24 pb-32 md:pt-32 md:pb-40">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-2xl">
                <Sparkles className="w-10 h-10 text-white" />
              </div>
            </div>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight">
              {t('landing.hero.title')}
            </h1>
            
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              {t('landing.hero.subtitle')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
              <Button 
                size="lg" 
                onClick={() => navigate("/auth?mode=signup")}
                className="text-lg px-8 bg-white text-primary hover:bg-white/90 shadow-xl"
              >
                {t('landing.hero.ctaPrimary')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => navigate("/auth")}
                className="text-lg px-8 border-2 border-white/30 bg-white/10 text-primary hover:bg-white/20"
              >
                {t('landing.hero.ctaSecondary')}
              </Button>
            </div>
          </div>
        </div>

        {/* Wave Separator */}
        <div className="absolute bottom-0 left-0 right-0 -mb-px">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" preserveAspectRatio="none">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="hsl(var(--background))" stroke="none"/>
          </svg>
        </div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-4 py-16 md:py-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              {t('landing.features.title')}
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="group border-2 hover:border-primary/50 transition-all duration-300 hover:shadow-xl animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardHeader>
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <feature.icon className="w-7 h-7 text-white" strokeWidth={2.5} />
                  </div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base leading-relaxed">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="bg-gradient-to-br from-secondary/30 to-muted/30 py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {t('landing.process.title')}
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {steps.map((step, index) => (
                <div 
                  key={index} 
                  className="flex flex-col gap-4 p-6 rounded-2xl bg-card border-2 hover:border-primary/50 transition-all duration-300 hover:shadow-lg animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary to-accent text-white flex items-center justify-center font-bold text-xl shadow-lg">
                    {step.number}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="container mx-auto px-4 py-16 md:py-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              {t('landing.value.title')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {t('landing.value.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="border-2 hover:border-primary/50 transition-all duration-300 hover:shadow-xl">
              <CardHeader>
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-4">
                  <Target className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-xl">{t('landing.value.specialized.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base leading-relaxed">
                  {t('landing.value.specialized.description')}
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-primary/50 transition-all duration-300 hover:shadow-xl">
              <CardHeader>
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-4">
                  <Library className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-xl">{t('landing.value.library.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base leading-relaxed">
                  {t('landing.value.library.description')}
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-primary/50 transition-all duration-300 hover:shadow-xl">
              <CardHeader>
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-4">
                  <Lightbulb className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-xl">{t('landing.value.prompts.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base leading-relaxed">
                  {t('landing.value.prompts.description')}
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-primary/50 transition-all duration-300 hover:shadow-xl">
              <CardHeader>
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-4">
                  <Zap className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-xl">{t('landing.value.available247.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base leading-relaxed">
                  {t('landing.value.available247.description')}
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-primary/50 transition-all duration-300 hover:shadow-xl">
              <CardHeader>
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-4">
                  <RotateCcw className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-xl">{t('landing.value.continuous.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base leading-relaxed">
                  {t('landing.value.continuous.description')}
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-primary/50 transition-all duration-300 hover:shadow-xl">
              <CardHeader>
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-4">
                  <BadgeEuro className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-xl">{t('landing.value.pricing.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base leading-relaxed">
                  {t('landing.value.pricing.description', { amount: pricing.amount })}
                </CardDescription>
              </CardContent>
            </Card>
          </div>
          
          {/* Honesty Section */}
          <div className="mt-20 max-w-4xl mx-auto">
            <Card className="p-10 border-4 border-primary/40 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/5 shadow-2xl">
              <div className="text-center space-y-6">
                <h3 className="text-3xl md:text-4xl font-bold text-foreground">
                  {t('landing.value.honesty.title')}
                </h3>
                <p className="text-lg md:text-xl text-foreground leading-relaxed max-w-2xl mx-auto font-medium">
                  {t('landing.value.honesty.description')}
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Combined Pricing & CTA Section */}
      <section className="container mx-auto px-4 py-16 md:py-20">
        <div className="max-w-4xl mx-auto">
          <Card className="relative overflow-hidden border-2 border-primary/20 bg-gradient-to-br from-primary/5 via-accent/5 to-transparent">
            <CardContent className="relative pt-16 pb-16 text-center space-y-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                <Clock className="w-8 h-8 text-primary" />
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold">
                {t('landing.cta.title')}
              </h2>
              
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                {t('landing.cta.subtitle')}
              </p>
              
              <div className="max-w-md mx-auto bg-card border-2 border-primary/30 rounded-2xl p-8 my-8">
                <div className="space-y-4">
                  <div>
                    <div className="text-5xl font-bold text-primary">€{pricing.amount}</div>
                    <div className="text-muted-foreground mt-1">{t('landing.cta.pricePerMonth')}</div>
                  </div>
                  <div className="pt-4 space-y-3 text-left">
                    <div className="flex items-start gap-3">
                      <Sparkles className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{t('landing.cta.feature1')}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Sparkles className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{t('landing.cta.feature2')}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Sparkles className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{t('landing.cta.feature3')}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Sparkles className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{t('landing.cta.feature4')}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <Button 
                  size="lg" 
                  onClick={() => navigate("/auth?mode=signup")}
                  className="text-lg px-8 shadow-lg"
                >
                  {t('landing.cta.button')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
              
              <p className="text-xs text-muted-foreground mt-4">
                {t('landing.cta.disclaimer')}
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 text-center bg-gradient-to-br from-primary via-primary/95 to-accent">
        <p className="text-lg text-white">
          {t('footer.text')}{" "}
          <a 
            href="https://junaco.de" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-white hover:underline font-semibold underline decoration-white/50"
          >
            {t('footer.linkText')}
          </a>
        </p>
      </footer>
    </div>
  );
}
